import './PhotoUploader.scss'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import Button from '../Button/Button'
import { ReactComponent as PhotoPlaceholder } from './PhotoPlaceholder.svg'
import classNames from 'classnames'
import { formEvent } from '../Form/Form'
import { gestureEvent } from '../GestureHandler/GestureHandler'

const PhotoUploader = ({ name, label, hint, source, size = 96, required }) => {
  const [imageData, setImageData] = useState()
  const [fileData, setFileData] = useState()
  const [editMode, setEditMode] = useState()
  const [selectedSize, setSelectedSize] = useState()
  const [selectedX, setSelectedX] = useState()
  const [selectedY, setSelectedY] = useState()
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [applyOffset, setApplyOffset] = useState()
  const fileInputRef = useRef()
  const canvasRef = useRef()
  const imageRef = useRef()

  const propagateValue = useCallback(() => {
    if (name && fileData) {
      fileInputRef.current.dispatchEvent(formEvent({
        action: 'PENDING',
        name,
        value: {
          ...fileData,
          type: 'image/png',
          get: async () => {
            return new Promise((resolve, reject) => {
              canvasRef.current.toBlob(resolve,'image/png')
            })
          }
        }
      }))
    }
  }, [name, fileData])

  useEffect(propagateValue, [propagateValue])

  useEffect(() => {
    if (name) {
      fileInputRef.current.dispatchEvent(formEvent({
        action: 'REQUIRED',
        name,
        required
      }))
    }
  }, [name, required])

  useEffect(() => {
    const _element = fileInputRef.current
    return () => {
      if (name) {
        _element.dispatchEvent(formEvent({
          action: 'REMOVE',
          name
        }))
      }
    }
  }, [name])

  const handleEdit = useCallback(() => {
    fileInputRef.current.click()
  }, [])

  const handleFileSelect = useCallback(() => {
    const file = fileInputRef.current.files[0]
    setImageData(URL.createObjectURL(file))
    const { name, type, size } = file
    setFileData({ name, type, size })
    setEditMode(true)
  }, [])

  const handleLoad = useCallback(() => {
    const bestSize = Math.min(
      imageRef.current.naturalWidth,
      imageRef.current.naturalHeight
    )
    const sx = Math.abs((imageRef.current.naturalWidth - bestSize) / 2)
    const sy = Math.abs((imageRef.current.naturalHeight - bestSize) / 2)
    setSelectedSize(bestSize)
    setSelectedX(sx)
    setSelectedY(sy)
  }, [])

  const handleUp = useCallback(() => setApplyOffset(true), [])

  const handleMove = useCallback((x, y) => setOffset({ x, y }), [])

  const handleMouseDown = useCallback(
    (event) => {
      event.preventDefault()
      canvasRef.current.dispatchEvent(
        gestureEvent({
          action: 'MOUSE-DOWN',
          x: event.clientX - offset.x,
          y: event.clientY - offset.y,
          move: handleMove,
          up: handleUp
        })
      )
    },
    [handleMove, handleUp, offset]
  )

  const handlePointerDown = useCallback(
    (event) => {
      event.preventDefault()
      canvasRef.current.dispatchEvent(
        gestureEvent({
          action: 'POINTER-DOWN',
          x: event.clientX - offset.x,
          y: event.clientY - offset.y,
          move: handleMove,
          up: handleUp
        })
      )
    },
    [handleMove, handleUp, offset]
  )

  useEffect(() => {
    if (applyOffset) {
      const [x, y] = calculatePosition({
        size,
        selectedSize,
        selectedX,
        selectedY,
        offset,
        image: imageRef.current
      })
      setSelectedX(x)
      setSelectedY(y)
      setOffset({ x: 0, y: 0 })
      setApplyOffset(false)
    }
  }, [applyOffset, size, selectedSize, selectedX, selectedY, offset])

  useEffect(() => {
    if (editMode) {
      const [x, y] = calculatePosition({
        size,
        selectedSize,
        selectedX,
        selectedY,
        offset,
        image: imageRef.current
      })
      const context = canvasRef.current.getContext('2d')
      context.drawImage(
        imageRef.current,
        x,
        y,
        selectedSize,
        selectedSize,
        0,
        0,
        size,
        size
      )
    }
  }, [editMode, selectedX, selectedY, selectedSize, size, offset])

  useEffect(() => {
    const oldImageData = imageData
    return () => {
      if (oldImageData) {
        URL.revokeObjectURL(oldImageData)
      }
    }
  }, [imageData])

  return (
    <div className='photo-uploader'>
      <div className='-label'>
        <div className='label-small-medium -text'>{label}</div>
        {hint && <div className='label-small-basic -hint'>{hint}</div>}
        <input
          ref={fileInputRef}
          type='file'
          accept='image/jpg,image/jpeg,image/png,image/gif,image/bmp,image/tiff'
          onChange={handleFileSelect}
        />
      </div>
      <div className="-box" style={{ width: size + "px", height: size + "px" }}>
        <div className="-photo">
          {(!source && !editMode) && <PhotoPlaceholder /> }
          {(imageData || source) && <img ref={imageRef} className={classNames({ "-hidden": imageData })} src={imageData || source} alt="" onLoad={handleLoad} />}
          {editMode && <canvas className="-edit" ref={canvasRef} width={size} height={size} onMouseDown={handleMouseDown} onPointerDown={handlePointerDown} />}
        </div>
        <div className="-buttons">
          <Button type="light" color="neutral" size="field-sized" icon="EditIcon" onClick={handleEdit} />
        </div>
      </div>
    </div>
  )
}

const calculatePosition = ({
  size,
  selectedSize,
  selectedX,
  selectedY,
  offset,
  image
}) => {
  const pixelStep = selectedSize / size
  let x = selectedX - pixelStep * offset.x
  if (x < 0) x = 0
  if (x + selectedSize > image.naturalWidth)
    x = image.naturalWidth - selectedSize
  let y = selectedY - pixelStep * offset.y
  if (y < 0) y = 0
  if (y + selectedSize > image.naturalHeight)
    y = image.naturalHeight - selectedSize
  return [x, y]
}

export default PhotoUploader
